// Documents tab — upload, preview, download and delete files attached to a lead.
// Files are private: downloads go through the authenticated API (blob fetch),
// never a public link. Accepts PDF, PNG, JPG, DOC/DOCX and XLSX up to 25 MB.

import React, { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Download, Eye, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { api } from '../../../lib/api.js';
import { fmtDate, fmtFromNow } from '../../../lib/format.js';

export const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xlsx';
const ALLOWED_EXT = /\.(pdf|png|jpe?g|docx?|xlsx)$/i;

/** @param {number} bytes @returns {string} e.g. "1.4 MB". */
export function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(mime = '') {
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime.includes('spreadsheet')) return FileSpreadsheet;
  return FileText;
}

export default function DocumentsTab({ lead, leadId, onChange }) {
  const id = leadId || lead?._id;
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [busyDoc, setBusyDoc] = useState(null);
  const docs = [...(lead?.documents || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  function refresh() {
    qc.invalidateQueries({ queryKey: ['lead', id] });
    onChange?.();
  }

  async function uploadFiles(files) {
    for (const file of Array.from(files || [])) {
      if (!ALLOWED_EXT.test(file.name)) { toast.error(`${file.name}: only PDF, PNG, JPG, DOC/DOCX and XLSX are allowed`); continue; }
      if (file.size > MAX_BYTES) { toast.error(`${file.name} is over 25 MB`); continue; }
      setUploading(true);
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post(`/leads/${id}/documents`, form);
        toast.success(`Uploaded ${file.name}`);
        refresh();
      } catch (e) {
        toast.error(e?.response?.data?.error || `Could not upload ${file.name}`);
      } finally {
        setUploading(false);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  /** Fetch the file with the auth header, then open it or save it. */
  async function fetchBlob(doc, inline) {
    setBusyDoc(doc._id);
    try {
      const res = await api.get(`/leads/${id}/documents/${doc._id}/download${inline ? '?inline=1' : ''}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (inline) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = doc.name; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Could not open that file');
    } finally {
      setBusyDoc(null);
    }
  }

  async function remove(doc) {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    setBusyDoc(doc._id);
    try {
      await api.delete(`/leads/${id}/documents/${doc._id}`);
      toast.success('Document deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not delete the document');
    } finally {
      setBusyDoc(null);
    }
  }

  return (
    <div
      className={`space-y-3 p-1 h-full overflow-y-auto rounded-lg transition ${dragging ? 'ring-2 ring-civix-purple/60 bg-civix-purple/5' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
      data-testid="documents-dropzone"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white/85">Documents</h3>
          <p className="text-[11px] text-white/45">Click upload or drag files here. PDF, PNG, JPG, DOCX, XLSX up to 25 MB.</p>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 shrink-0 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload document'}
        </button>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
      </div>

      {docs.length === 0 ? (
        <p className="text-xs text-white/40 p-6 text-center border border-dashed border-civix-border rounded-lg">
          No files available. Drag files here to upload.
        </p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const Icon = iconFor(d.mimeType);
            const previewable = /^(application\/pdf|image\/(png|jpeg))$/.test(d.mimeType || '');
            const busy = busyDoc === d._id;
            return (
              <li key={d._id} className="flex items-center gap-3 rounded-lg bg-civix-panel border border-civix-border px-3 py-2">
                <Icon className="w-4 h-4 text-civix-purpleSoft shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-white/85 truncate" title={d.name}>{d.name}</div>
                  <div className="text-[10px] text-white/40" title={d.createdAt ? fmtDate(d.createdAt) : ''}>
                    {fmtSize(d.size)} · {d.createdAt ? fmtFromNow(d.createdAt) : ''}
                  </div>
                </div>
                {busy ? <Loader2 className="w-4 h-4 animate-spin text-white/50" /> : (
                  <div className="flex items-center gap-2 text-white/50">
                    {previewable && <button type="button" onClick={() => fetchBlob(d, true)} title="Preview" aria-label={`Preview ${d.name}`} className="hover:text-civix-cyan"><Eye className="w-4 h-4" /></button>}
                    <button type="button" onClick={() => fetchBlob(d, false)} title="Download" aria-label={`Download ${d.name}`} className="hover:text-civix-cyan"><Download className="w-4 h-4" /></button>
                    <button type="button" onClick={() => remove(d)} title="Delete" aria-label={`Delete ${d.name}`} className="hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
